import type { DataGridFilterState, DataGridSortingState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback } from 'react';
import type { TableSearchState } from '@/entities/settings';
import { fromDataGridFilters, fromDataGridSorting } from '@/entities/settings';

/** Параметры обработчиков строки поиска, сортировки и фильтров списка */
type Params = {
  searchMode: 'nl' | 'dsl';
  /** Текущие значения поиска: NL (`human`) и DSL */
  searchValue: { human: string; dsl: string };
  setSearchMode: (mode: 'nl' | 'dsl') => void;
  /** Сбрасывает состояние smart-search */
  clearSearch: () => void;
  /** Применяет сортировку через состояние поиска по содержимому */
  onContentSearchSortingChange: (sorting: TableSortingState) => void;
  /** Записывает фильтры таблицы */
  applyFilters: (filters: TableFilterState) => void;
  /** Записывает основной поиск */
  commitSearch: (search: TableSearchState | null) => void;
  /** Ручной триггер перезагрузки списка */
  triggerReload: () => void;
};

/** Обработчики строки поиска, сортировки и фильтров грида */
type Result = {
  onSortingChange: (next: DataGridSortingState) => void;
  onFiltersChange: (next: DataGridFilterState) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onModeChange: (mode: 'nl' | 'dsl') => void;
};

/**
 * Обработчики строки поиска и сортировки/фильтров грида: адаптируют формат грида (DataGrid*State)
 * к формату таблицы (Table*State) и применяют изменения к состоянию списка.
 */
export const useDialogSearchHandlers = ({
  searchMode,
  searchValue,
  setSearchMode,
  clearSearch,
  onContentSearchSortingChange,
  applyFilters,
  commitSearch,
  triggerReload,
}: Params): Result => {
  const onSortingChange = useCallback(
    (next: DataGridSortingState) => {
      onContentSearchSortingChange(fromDataGridSorting(next));
    },
    [onContentSearchSortingChange],
  );

  const onFiltersChange = useCallback(
    (next: DataGridFilterState) => {
      applyFilters(fromDataGridFilters(next));
    },
    [applyFilters],
  );

  const onSearch = useCallback(() => {
    const query = searchMode === 'nl' ? searchValue.human : searchValue.dsl;
    commitSearch(query.trim() ? { query: query.trim(), mode: searchMode } : null);
    triggerReload();
  }, [searchMode, searchValue.human, searchValue.dsl, commitSearch, triggerReload]);

  const onClearSearch = useCallback(() => {
    clearSearch();
    commitSearch(null);
    triggerReload();
  }, [clearSearch, commitSearch, triggerReload]);

  const onModeChange = useCallback(
    (mode: 'nl' | 'dsl') => {
      setSearchMode(mode);
    },
    [setSearchMode],
  );

  return { onSortingChange, onFiltersChange, onSearch, onClearSearch, onModeChange };
};
